import React from 'react';
import { SpaceBetween, Container, Header, Box, KeyValuePairs } from '@amzn/awsui-components-console';
import { AttributeData } from '@amzn/snow-data-service-typescript-client';
import { CombinedOperation } from 'src/@types/serviceOnboardingTypes/operation.interface';
import './OperationDetails.scss';

interface KeyValueItem {
	label: string;
	value: React.ReactNode;
}

const formatJson = (jsonString: string) => {
	try {
		return JSON.stringify(JSON.parse(jsonString), null, 2);
	} catch (e) {
		return jsonString;
	}
};

const formatMap = (map: Record<string, string | Record<string, string> | string[]> | undefined) => {
	if (!map || Object.keys(map).length === 0) return '';
	try {
		return JSON.stringify(map, null, 2);
	} catch (e) {
		console.warn('Failed to format map:', e);
		return String(map);
	}
};

const FormattedJsonBlock: React.FC<{ label: string; content: string }> = ({ label, content }) => (
	<div>
		<Box variant="awsui-key-label">{label}</Box>
		<pre className="formatted-json-block">{content}</pre>
	</div>
);

const getTaskInputItems = (inputs: AttributeData[] | undefined): KeyValueItem[] => {
	if (!inputs || inputs.length === 0) return [];

	return inputs.filter(Boolean).map((input) => ({
		label: input.attributeName || 'Unknown',
		value: (
			<SpaceBetween direction="vertical" size="xs">
				<div>
					<strong>Type:</strong> {input.attributeType || 'Unknown'}
				</div>
				{input.description && (
					<div>
						<strong>Description:</strong> {input.description}
					</div>
				)}
				{input.value !== undefined && (
					<div>
						<strong>Default Value:</strong> {input.value}
					</div>
				)}
			</SpaceBetween>
		),
	}));
};

const getTaskOutputItems = (outputs: AttributeData[] | undefined): KeyValueItem[] => {
	if (!outputs || outputs.length === 0) return [];

	return outputs.filter(Boolean).map((output) => ({
		label: output.attributeName || 'Unknown',
		value: (
			<SpaceBetween direction="vertical" size="xs">
				<div>
					<strong>Type:</strong> {output.attributeType || 'Unknown'}
				</div>
				{output.description && (
					<div>
						<strong>Description:</strong> {output.description}
					</div>
				)}
				{output.value !== undefined && (
					<div>
						<strong>Default Value:</strong> {output.value}
					</div>
				)}
			</SpaceBetween>
		),
	}));
};

export const getOperationDetailsItems = (
	operationData: CombinedOperation,
	operationId: string
) => {
	const operationInfoItems: KeyValueItem[] = [
		{ label: 'Operation ID', value: operationId },
		{ label: 'Operation Name', value: operationData.operationName || '' },
		{ label: 'Version', value: operationData.version || '' },
		{ label: 'Description', value: operationData.description || '' },
		{ label: 'Status', value: operationData.status || '' },
		{ label: 'Integration Type', value: operationData.integrationType || '' },
		{ label: 'Idempotent', value: operationData.idempotent ? 'Yes' : 'No' },
		{ label: 'Execution Type', value: operationData.executionType || '' },
		...(operationData.operationAccessBindles && operationData.operationAccessBindles.length > 0
			? [{ label: 'Operation Access Bindles', value: operationData.operationAccessBindles.join(', ') }]
			: []),
	];

	const requestDetailsItems: KeyValueItem[] = [
		{
			label: 'Operation Auth Type',
			value: operationData.requestObject?.operationAuthType || '',
		},
		...(operationData.requestObject?.timeout
			? [
				{
					label: 'Timeout (seconds)',
					value: String(operationData.requestObject.timeout),
				},
			  ]
			: []),
		...(operationData.requestObject?.protocol
			? [{ label: 'Protocol', value: operationData.requestObject.protocol }]
			: []),
		...(operationData.requestObject?.encoding
			? [{ label: 'Encoding', value: operationData.requestObject.encoding }]
			: []),
	];

	const apiGatewayItems: KeyValueItem[] = operationData.requestObject?.apiGateWayRequestParameters
		? [
			{
				label: 'HTTP Method',
				value: operationData.requestObject.apiGateWayRequestParameters.httpMethod || '',
			},
			{
				label: 'Stage',
				value: operationData.requestObject.apiGateWayRequestParameters.stage || '',
			},
			{
				label: 'Path',
				value: operationData.requestObject.apiGateWayRequestParameters.path || '',
			},
			{
				label: 'Auth Type',
				value: operationData.requestObject.apiGateWayRequestParameters.authType || '',
			},
			...(operationData.requestObject.apiGateWayRequestParameters.authCredential
				? [{
					label: 'Auth Credential',
					value: operationData.requestObject.apiGateWayRequestParameters.authCredential,
				  }]
				: []),
			...(Object.keys(operationData.requestObject.apiGateWayRequestParameters.queryParameters ?? {}).length > 0
				? [{
					label: 'Query Parameters',
					value: formatMap(operationData.requestObject.apiGateWayRequestParameters.queryParameters),
				  }]
				: []),
			...(Object.keys(operationData.requestObject.apiGateWayRequestParameters.headers ?? {}).length > 0
				? [{
					label: 'Headers',
					value: formatMap(operationData.requestObject.apiGateWayRequestParameters.headers as Record<string, string[]>),
				  }]
				: []),
		  ]
		: [];

	const awsAuthItems: KeyValueItem[] = operationData.requestObject?.awsAuthParameters
		? [
			{
				label: 'Signing Region',
				value: operationData.requestObject.awsAuthParameters.signingRegion || '',
			},
			{
				label: 'IAM Role ARN',
				value: operationData.requestObject.awsAuthParameters.iamRoleArn || '',
			},
			{
				label: 'HTTP Method',
				value: operationData.requestObject.awsAuthParameters.method || '',
			},
			{
				label: 'Service',
				value: operationData.requestObject.awsAuthParameters.service || '',
			},
		  ]
		: [];

	const serviceInfoItems: KeyValueItem[] = [
		...(operationData.serviceNamespace
			? [{ label: 'Service Namespace', value: operationData.serviceNamespace }]
			: []),
		...(operationData.operationNamespace
			? [{ label: 'Operation Namespace', value: operationData.operationNamespace }]
			: []),
		...(operationData.aaaServiceName
			? [{ label: 'AAA Service Name', value: operationData.aaaServiceName }]
			: []),
		...(operationData.superStarServiceName
			? [{ label: 'SuperStar Service Name', value: operationData.superStarServiceName }]
			: []),
		...(operationData.configurations && operationData.configurations.length > 0
			? [{ label: 'Configurations', value: operationData.configurations.join(', ') }]
			: []),
	];

	const auditInfoItems: KeyValueItem[] = [
		{ label: 'Created By', value: operationData.createdBy || '' },
		{ label: 'Last Updated By', value: operationData.lastUpdatedBy || '' },
	];

	const taskInputItems = getTaskInputItems(operationData.taskInputs);
	const taskOutputItems = getTaskOutputItems(operationData.taskOutputs);

	const requestBodySection = (
		<FormattedJsonBlock
			label="Request Body"
			content={formatJson(operationData.requestObject?.requestBody || '{}')}
		/>
	);

	const responseBodySection = (
		<FormattedJsonBlock
			label="Response Body"
			content={formatJson(operationData.responseObject?.responseBody || '{}')}
		/>
	);

	const successCriteriaSection = (
		<FormattedJsonBlock
			label="Success Criteria"
			content={formatJson(operationData.responseObject?.successCriteria || '{}')}
		/>
	);

	const failureCriteriaSection = operationData.responseObject?.failureCriteria ? (
		<FormattedJsonBlock
			label="Failure Criteria"
			content={formatJson(operationData.responseObject.failureCriteria)}
		/>
	) : null;

	const stageEndpointsMappingSection =
		Object.keys(operationData.requestObject?.stageToEndpointsMapping ?? {}).length > 0 ? (
			<FormattedJsonBlock
				label="Stage to Endpoints Mapping"
				content={formatMap(operationData.requestObject!.stageToEndpointsMapping as Record<string, Record<string, string>>)}
			/>
		) : null;

	const stageDefaultRegionMappingSection =
		Object.keys(operationData.requestObject?.stageToDefaultRegionMapping ?? {}).length > 0 ? (
			<FormattedJsonBlock
				label="Stage to Default Region Mapping"
				content={formatMap(operationData.requestObject!.stageToDefaultRegionMapping)}
			/>
		) : null;

	return {
		operationInfoItems,
		requestDetailsItems,
		apiGatewayItems,
		awsAuthItems,
		serviceInfoItems,
		auditInfoItems,
		taskInputItems,
		taskOutputItems,
		requestBodySection,
		responseBodySection,
		successCriteriaSection,
		failureCriteriaSection,
		stageEndpointsMappingSection,
		stageDefaultRegionMappingSection,
	};
};

export const renderTaskInputSection = (taskInputItems: KeyValueItem[]) => {
	if (taskInputItems.length === 0) return null;

	return (
		<Container header={<Header variant="h3">Task Input Parameters</Header>}>
			<KeyValuePairs
				columns={1}
				items={taskInputItems}
				data-testid="task-input-items-key-value-pairs"
			/>
		</Container>
	);
};

export const renderTaskOutputSection = (taskOutputItems: KeyValueItem[]) => {
	if (taskOutputItems.length === 0) return null;

	return (
		<Container header={<Header variant="h3">Task Output Parameters</Header>}>
			<KeyValuePairs
				columns={1}
				items={taskOutputItems}
				data-testid="task-output-items-key-value-pairs"
			/>
		</Container>
	);
};
